// Common API client utilities for GET and POST requests
import type { TokenInfo } from '../users/types'
import { ApiError } from './ApiError'
import { logger } from './logger'
import { apiErrorMessages } from './texts'

export interface ApiClientDependencies {
  fetch: typeof window.fetch
  env: {
    DESTINATIONS_TOOL_BACKEND_BASE_URL: string
  }
}

export interface ApiClient {
  apiGet: <TResponseData>(url: string, tokenInfo: TokenInfo) => Promise<TResponseData | null>
  apiPost: <TResponseData, TRequestBody>(url: string, body: TRequestBody, tokenInfo: TokenInfo) => Promise<TResponseData>
  apiPatch: <TResponseData, TRequestBody>(url: string, body: TRequestBody, tokenInfo: TokenInfo) => Promise<TResponseData>
  apiDelete: <TResponseData>(url: string, tokenInfo: TokenInfo) => Promise<TResponseData | null>
}

interface JsonApiResponseBase {
  errors?: JsonApiError[] | null
  meta?: JsonApiMeta | null
  links?: JsonApiLinks | null
}

interface JsonApiResponse<TData> extends JsonApiResponseBase {
  data?: TData | null
}

interface JsonApiError {
  id: string | null
  status: number | null
  code: string | null
  title: string | null
  detail: string | null
}

interface JsonApiMeta {
  code: string
  status: string
  sysdate: Date
}

interface JsonApiLinks {
  self: string
}

async function getJsonApi<TData>(response: Response): Promise<JsonApiResponse<TData>> {
  return await response.json?.() as JsonApiResponse<TData> ?? {}
}

async function getJsonApiNoData(response: Response): Promise<JsonApiResponseBase> {
  return await response.json?.() as JsonApiResponseBase ?? {}
}

function getErrorMessages(errors: JsonApiError[] | null | undefined): string | null {
  const errorMessages: string | null = errors?.map(error => `[${error.title}] ${error.detail}`).join(' | ') ?? null

  return errorMessages
}

function getDataSize(data: any): number {
  if (data === undefined || data === null || data === '') {
    return 0
  }

  const dataAsString = JSON.stringify(data)
  const sizeInBytes = new TextEncoder().encode(dataAsString).length

  return sizeInBytes
}

export function createApiClient(dependencies: ApiClientDependencies) {
  const { fetch } = dependencies

  async function apiGet<TResponseData>(url: string, tokenInfo: { tokenType: string, accessToken: string }): Promise<TResponseData | null> {
    try {
      const response: Response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `${tokenInfo.tokenType} ${tokenInfo.accessToken}`,
        },
      })

      const statusCode: number = response.status

      if (statusCode === 400) {
        const { errors } = await getJsonApiNoData(response)

        throw new ApiError(400, getErrorMessages(errors) ?? apiErrorMessages.badRequest)
      }
      if (statusCode === 401) {
        throw new ApiError(401, apiErrorMessages.unauthorized)
      }
      if (statusCode === 403) {
        throw new ApiError(403, apiErrorMessages.forbidden)
      }
      if (statusCode === 404) {
        throw new ApiError(404, apiErrorMessages.endpointNotFound)
      }

      if (statusCode === 200) {
        const { data } = await getJsonApi<TResponseData>(response)

        return data ?? null // Ensure undefined data is returned as null to enforce the method signature.
      }

      throw new ApiError(statusCode, `API GET request could not be executed, API responded with code ${statusCode}`)
    }
    catch (error) {
      if (error instanceof ApiError) {
        throw error
      }

      logger.logError(error as Error)
      throw new Error(apiErrorMessages.connectionError)
    }
  }

  async function apiPost<TResponseData, TRequestBody>(url: string, body: TRequestBody, tokenInfo: { tokenType: string, accessToken: string }): Promise<TResponseData> {
    try {
      const response: Response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${tokenInfo.tokenType} ${tokenInfo.accessToken}`,
        },
        body: JSON.stringify(body),
      })

      const statusCode: number = response.status

      if (statusCode === 400) {
        const { errors } = await getJsonApiNoData(response)

        throw new ApiError(400, getErrorMessages(errors) ?? apiErrorMessages.badRequest)
      }
      if (statusCode === 401) {
        throw new ApiError(401, apiErrorMessages.unauthorized)
      }
      if (statusCode === 403) {
        throw new ApiError(403, apiErrorMessages.forbidden)
      }
      if (statusCode === 404) {
        throw new ApiError(404, apiErrorMessages.endpointNotFound)
      }

      const { data } = await getJsonApi<TResponseData>(response)

      if (statusCode === 200 || statusCode === 201) {
        if (data === null || data === undefined) {
          throw new ApiError(statusCode, `API POST request returned no data. Size of the request body was ${getDataSize(data)} bytes.`)
        }
        return data
      }

      throw new ApiError(statusCode, `API POST request could not be executed, API responded with code ${statusCode}`)
    }
    catch (error) {
      if (error instanceof ApiError) {
        throw error
      }

      logger.logError(error as Error)
      throw new Error(apiErrorMessages.connectionError)
    }
  }

  async function apiPatch<TResponseData, TRequestBody>(url: string, body: TRequestBody, tokenInfo: { tokenType: string, accessToken: string }): Promise<TResponseData> {
    try {
      const response: Response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${tokenInfo.tokenType} ${tokenInfo.accessToken}`,
        },
        body: JSON.stringify(body),
      })

      const statusCode: number = response.status

      if (statusCode === 400) {
        const { errors } = await getJsonApiNoData(response)

        throw new ApiError(400, getErrorMessages(errors) ?? apiErrorMessages.badRequest)
      }
      if (statusCode === 401) {
        throw new ApiError(401, apiErrorMessages.unauthorized)
      }
      if (statusCode === 403) {
        throw new ApiError(403, apiErrorMessages.forbidden)
      }
      if (statusCode === 404) {
        throw new ApiError(404, apiErrorMessages.endpointNotFound)
      }
      if (statusCode !== 200) {
        throw new ApiError(statusCode, `API PATCH request could not be executed, API responded with code ${statusCode}`)
      }

      const { data } = await getJsonApi<TResponseData>(response)
      if (data === null || data === undefined) {
        throw new ApiError(statusCode, `API PATCH request returned no data. Size of the request body was ${getDataSize(data)} bytes.`)
      }
      return data
    }
    catch (error) {
      if (error instanceof ApiError) {
        throw error
      }

      logger.logError(error as Error)
      throw new Error(apiErrorMessages.connectionError)
    }
  }

  async function apiDelete<TResponseData>(url: string, tokenInfo: { tokenType: string, accessToken: string }): Promise<TResponseData | null> {
    try {
      const response: Response = await fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `${tokenInfo.tokenType} ${tokenInfo.accessToken}`,
        },
      })

      const statusCode: number = response.status

      if (statusCode === 400) {
        const { errors } = await getJsonApiNoData(response)

        throw new ApiError(400, getErrorMessages(errors) ?? apiErrorMessages.badRequest)
      }
      if (statusCode === 401) {
        throw new ApiError(401, apiErrorMessages.unauthorized)
      }
      if (statusCode === 403) {
        throw new ApiError(403, apiErrorMessages.forbidden)
      }
      if (statusCode === 404) {
        return null // There was no content to delete (or it was already deleted).
      }

      if (statusCode === 200) {
        const { data } = await getJsonApi<TResponseData>(response)

        return data ?? null // Ensure undefined data is returned as null to enforce the method signature.
      }

      throw new ApiError(statusCode, `API DELETE request could not be executed, API responded with code ${statusCode}`)
    }
    catch (error) {
      if (error instanceof ApiError) {
        throw error
      }

      logger.logError(error as Error)

      throw new Error(apiErrorMessages.connectionError)
    }
  }

  return { apiGet, apiPost, apiPatch, apiDelete }
}
